import type {
  ApprovalRule,
  ApprovalRuleType,
  CreateRulePayload,
} from '../types/approval-workflow.types';

/**
 * Condiciones de una regla de aprobación.
 *
 * Una regla puede combinar varias (LOB + margen, monto + segmento…) y aplica
 * cuando se cumplen todas. `ruleType` quedó en la base como etiqueta de la
 * condición principal, pero lo que manda es qué campos tienen valor.
 */

/** Orden canónico: el mismo en el formulario, en la tabla y para `ruleType`. */
export const CONDICIONES: ApprovalRuleType[] = [
  'AMOUNT_THRESHOLD',
  'GLOBAL_MARGIN',
  'LOB',
  'CUSTOMER_SEGMENT',
  'PRICING_MODEL',
  'PRESUPUESTO_CLIENTE',
];

export const ruleTypeLabels: Record<ApprovalRuleType, string> = {
  AMOUNT_THRESHOLD: 'Monto',
  GLOBAL_MARGIN: 'Margen Global',
  LOB: 'Línea de Negocio',
  CUSTOMER_SEGMENT: 'Segmento de Cliente',
  PRICING_MODEL: 'Modelo de Precios',
  PRESUPUESTO_CLIENTE: 'Supera el presupuesto del cliente',
};

export const segmentLabels: Record<string, string> = {
  ENTERPRISE: 'Enterprise',
  SMB: 'PyME',
  GOBIERNO: 'Gobierno',
  STARTUP: 'Startup',
};

/** Qué condiciones tiene cargadas una regla guardada. */
export function condicionesDeRegla(rule: ApprovalRule): ApprovalRuleType[] {
  return CONDICIONES.filter((c) => {
    switch (c) {
      case 'AMOUNT_THRESHOLD':
        return rule.amountThreshold != null;
      case 'GLOBAL_MARGIN':
        return rule.marginThreshold != null;
      case 'LOB':
        return !!rule.lobId;
      case 'CUSTOMER_SEGMENT':
        return !!rule.customerSegment;
      case 'PRICING_MODEL':
        return !!rule.pricingModelId;
      case 'PRESUPUESTO_CLIENTE':
        return rule.condicionPresupuesto === true;
    }
  });
}

function describirCondicion(rule: ApprovalRule, c: ApprovalRuleType): string {
  switch (c) {
    case 'AMOUNT_THRESHOLD':
      return `Monto > Bs ${rule.amountThreshold!.toLocaleString('es-BO')}`;
    case 'GLOBAL_MARGIN':
      return `Margen < ${rule.marginThreshold}%`;
    case 'LOB':
      return rule.lob?.name || rule.lobId!;
    case 'CUSTOMER_SEGMENT':
      return segmentLabels[rule.customerSegment!] || rule.customerSegment!;
    case 'PRICING_MODEL':
      return rule.pricingModel?.name || rule.pricingModelId!;
    case 'PRESUPUESTO_CLIENTE':
      return 'Total > presupuesto del cliente';
  }
}

/** Texto de la columna "Condición" de la tabla. */
export function describirCondiciones(rule: ApprovalRule): string {
  const partes = condicionesDeRegla(rule).map((c) => describirCondicion(rule, c));
  return partes.length > 0 ? partes.join(' y ') : '—';
}

/** Estado del formulario tal como lo tienen los inputs: todo en texto. */
export interface FormularioRegla {
  name: string;
  condiciones: ApprovalRuleType[];
  amountThreshold: string;
  marginThreshold: string;
  lobId: string;
  customerSegment: string;
  pricingModelId: string;
  requiredApprovers: string;
}

function valorDe(form: FormularioRegla, c: ApprovalRuleType): string {
  switch (c) {
    case 'AMOUNT_THRESHOLD':
      return form.amountThreshold;
    case 'GLOBAL_MARGIN':
      return form.marginThreshold;
    case 'LOB':
      return form.lobId;
    case 'CUSTOMER_SEGMENT':
      return form.customerSegment;
    case 'PRICING_MODEL':
      return form.pricingModelId;
    case 'PRESUPUESTO_CLIENTE':
      return 'sí'; // no lleva valor: es una casilla
  }
}

/**
 * Convierte el formulario en el payload para la API.
 *
 * Manda siempre las 5 condiciones (null en las no elegidas) para que editar
 * una regla y desmarcar una casilla realmente la borre. Devuelve `error` si
 * no hay ninguna condición o si alguna elegida quedó sin completar — antes de
 * llegar al backend, que es cuando se puede corregir.
 */
export function armarPayloadRegla(
  form: FormularioRegla
): { payload: CreateRulePayload } | { error: string } {
  const elegidas = CONDICIONES.filter((c) => form.condiciones.includes(c));
  if (elegidas.length === 0) return { error: 'Elegí al menos una condición' };

  const incompletas = elegidas.filter((c) => valorDe(form, c).trim() === '');
  if (incompletas.length > 0) {
    return { error: `Falta completar: ${incompletas.map((c) => ruleTypeLabels[c]).join(', ')}` };
  }

  const elegida = (c: ApprovalRuleType) => elegidas.includes(c);
  const numero = (texto: string) => Number.parseFloat(texto);

  return {
    payload: {
      name: form.name.trim(),
      // El servidor no conoce `PRESUPUESTO_CLIENTE` como tipo: sola, la regla queda etiquetada como de monto.
      ruleType: elegidas.find((c) => c !== 'PRESUPUESTO_CLIENTE') ?? 'AMOUNT_THRESHOLD',
      requiredApprovers: Math.max(1, Number.parseInt(form.requiredApprovers, 10) || 1),
      amountThreshold: elegida('AMOUNT_THRESHOLD') ? numero(form.amountThreshold) : null,
      marginThreshold: elegida('GLOBAL_MARGIN') ? numero(form.marginThreshold) : null,
      lobId: elegida('LOB') ? form.lobId : null,
      customerSegment: elegida('CUSTOMER_SEGMENT') ? form.customerSegment : null,
      pricingModelId: elegida('PRICING_MODEL') ? form.pricingModelId : null,
      condicionPresupuesto: elegida('PRESUPUESTO_CLIENTE'),
    },
  };
}
